import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDeterminateProgress } from './hooks';

interface StarProgressIndicatorProps {
  text: string;
  mode: 'determinate' | 'indeterminate';
  progress: number;
  speed: number;
}

// Enhanced keyframes for more noticeable shimmer effect
const shimmer = keyframes`
    0% {
        filter: brightness(0.9) drop-shadow(0 0 10px currentColor);
        opacity: 0.8;
    }
    50% {
        filter: brightness(1.5) drop-shadow(0 0 10px currentColor) drop-shadow(0 0 60px currentColor);
        opacity: 1;
    }
    100% {
        filter: brightness(0.9) drop-shadow(0 0 10px currentColor);
        opacity: 0.8;
    }
`;

// Styled component for the star container
const StarContainer = styled(Box)<{
  isIndeterminate: boolean;
  animationSpeed: number;
  starColor: string;
}>(
  ({
    theme: _theme,
    isIndeterminate,
    animationSpeed: _animationSpeed,
    starColor,
  }) => ({
    width: 120,
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color: starColor,
    margin: '0 auto',
    ...(isIndeterminate && {
      animation: `${shimmer} 2s ease-in-out infinite`,
    }),
  }),
);

// Styled SVG component
const StarSvg = styled('svg')({
  width: '100%',
  height: '100%',
  transform: 'rotate(-90deg)', // Start progress from top
});

/**
 * Star-based progress indicator with shimmer effect for indeterminate mode
 * and outline fill for determinate mode.
 */
export const StarProgressIndicator: React.FC<StarProgressIndicatorProps> = ({
  text,
  mode,
  progress: initialProgress,
  speed,
}) => {
  // TODO: Fix this, because loader is loaded before theme context is loaded!
  //       We see a blip of blue if we use the theme hook as intended.
  //       Hardcoded orange(s) works because only BRIGHT uses this loader (see index.tsx)
  //       We should still think about when theme is loaded for this loading bar
  // const theme = useTheme();
  const progress = useDeterminateProgress(mode, initialProgress, speed);

  // Get theme colors
  const primaryColor = '#F13C00';
  //theme.palette.primary.main;
  const primaryLight = '#ff6e3d';
  //theme.palette.primary.light;

  // Calculate stroke dash array for determinate progress
  // The path has an approximate length of 3500 units
  const pathLength = 3500;
  const strokeDasharray =
    mode === 'determinate'
      ? `${(progress / 100) * pathLength} ${pathLength}`
      : undefined;

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <StarContainer
        isIndeterminate={mode === 'indeterminate'}
        animationSpeed={speed}
        starColor={primaryColor}
      >
        <StarSvg
          viewBox="0 0 1029.62 1043.2"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="starGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={primaryLight} />
              <stop offset="100%" stopColor={primaryColor} />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Background star (always visible but faded) */}
          <path
            d="m531.04,0h-32.47C474.64,294.9,284.8,491.1,0,504.92v33.36c284.8,13.81,474.64,210.02,498.57,504.92h32.47c23.94-294.9,213.77-491.11,498.57-504.92v-33.36c-284.8-13.82-474.63-210.02-498.57-504.92Z"
            fill="none"
            stroke={primaryColor}
            strokeWidth="30"
            opacity="0.15"
          />

          {/* Progress/animated star */}
          <path
            d="m531.04,0h-32.47C474.64,294.9,284.8,491.1,0,504.92v33.36c284.8,13.81,474.64,210.02,498.57,504.92h32.47c23.94-294.9,213.77-491.11,498.57-504.92v-33.36c-284.8-13.82-474.63-210.02-498.57-504.92Z"
            fill={mode === 'indeterminate' ? primaryColor : 'none'}
            stroke={mode === 'determinate' ? primaryColor : 'none'}
            strokeWidth={mode === 'determinate' ? '20' : '0'}
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            filter={mode === 'indeterminate' ? 'url(#glow)' : undefined}
            style={{
              transition:
                mode === 'determinate'
                  ? 'stroke-dasharray 0.3s ease'
                  : undefined,
            }}
          />
        </StarSvg>
      </StarContainer>

      <Box sx={{ mt: 3 }}>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{
            fontWeight: 500,
            fontSize: { xs: '0.875rem', sm: '1.2rem' },
          }}
        >
          {text}
          {mode === 'determinate' && ` ${Math.round(progress)}%`}
        </Typography>
      </Box>
    </Box>
  );
};
